import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { SBHorizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Bezel } from '@sorare/core/src/atoms/ui/bezels/Bezel';
import { LightedBezel } from '@sorare/core/src/atoms/ui/bezels/LightedBezel';
import type { HexColor } from '@sorare/core/src/style/types';

const Root = styled.div`
  position: relative;
  isolation: isolate;

  &,
  & > * {
    width: 100%;
    height: 100%;
  }
`;
const FullSizeLightedBezel = styled(LightedBezel)`
  width: 100%;
  height: 100%;

  background-color: var(--c-black);
`;
const FullSizeBezel = styled(Bezel)`
  width: 100%;
  height: 100%;

  background-color: var(--c-black);
`;
const FullWidthVertical = styled(Vertical)`
  width: 100%;
  height: 100%;
`;
const LeagueHeader = styled.img`
  width: 100%;
  max-height: calc(5 * var(--unit));
`;
const Header = styled(SBHorizontal)`
  width: 100%;
  padding: var(--double-unit) var(--double-unit) 0 var(--double-unit);
`;
const Body = styled(Vertical).attrs({ gap: 5, center: true })`
  flex: 1;
`;

type Props = {
  icon?: ReactNode;
  body?: ReactNode;
  background?: string;
  borderColor?: string;
  leaguePictureUrl?: string;
  topRightIcon?: ReactNode;
  topLeftIcon?: ReactNode;
  color?: HexColor;
  onReveal?: () => void;
  revealed?: boolean;
};
export const RewardBase = ({
  icon,
  body,
  background,
  borderColor,
  leaguePictureUrl,
  topRightIcon,
  topLeftIcon,
  color,
  onReveal,
  revealed,
}: Props) => {
  const BezelComponent = revealed ? FullSizeBezel : FullSizeLightedBezel;

  return (
    <Root>
      <BezelComponent
        color={color}
        background={background}
        borderColor={borderColor}
      >
        <FullWidthVertical
          gap={2}
          center
          as={onReveal ? 'button' : 'div'}
          onClick={onReveal}
        >
          <Header>
            {topLeftIcon}
            {topRightIcon}
          </Header>
          <Body>
            {icon}
            {!revealed && (
              <Button size="small" color="quaternary" asSpan>
                <FormattedMessage
                  id="RewardBase.reveal.cta"
                  defaultMessage="Tap to reveal!"
                />
              </Button>
            )}
            {body}
          </Body>
          {!!leaguePictureUrl && <LeagueHeader src={leaguePictureUrl} />}
        </FullWidthVertical>
      </BezelComponent>
    </Root>
  );
};
